import React, { useMemo, useState } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { CircularProgress } from '@mui/material'
import { Sprint, SprintPacket, SprintPacketStatus } from '@marketing-milk/interfaces'
import { UserLayout } from 'components/layout/UserLayout'
import { FullPageHeader } from 'components/layout/FullPageLayout/Header/FullPageHeader'
import { FullPageContent } from 'components/layout/FullPageLayout/Content/FullPageContent'
import { useNest } from 'hooks/api/useNest'
import { useSprintPackets } from 'hooks/api/useSprintPackets'
import { NewSprintPacketModal } from './components/NewSprintPacketModal'

const statusLabel: Record<SprintPacketStatus, string> = {
  draft: 'Draft',
  ready: 'Ready',
  presented: 'Presented',
  archived: 'Archived',
}

const formatIsoDate = (iso: string) => {
  const d = new Date(iso)
  return Number.isNaN(d.getTime())
    ? iso
    : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
}

const formatSprintWindow = (s?: Sprint) => {
  if (!s) return '—'
  const start = new Date(s.startTime * 1000)
  const end = new Date(s.endTime * 1000)
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' }
  return `${start.toLocaleDateString(undefined, opts)} – ${end.toLocaleDateString(undefined, opts)}`
}

export function ListSprintPackets() {
  const { businessID } = useParams<{ businessID: string }>()
  const history = useHistory()
  const {
    list: packets,
    create,
    isLoading: packetsLoading,
    isError: packetsError,
    refetch: refetchPackets,
  } = useSprintPackets()
  const { list: sprints, isLoading: sprintsLoading } = useNest('sprints')
  const [modalOpen, setModalOpen] = useState(false)

  const sprintsById = useMemo(() => {
    const map = new Map<number, Sprint>()
    for (const s of sprints ?? []) map.set(s.id, s)
    return map
  }, [sprints])

  const disabledSprintIDs = useMemo(() => {
    return new Set((packets ?? []).map(p => p.sprintID))
  }, [packets])

  const loading = packetsLoading || sprintsLoading

  const handleCreate = async (sprintID: number) => {
    const created = await create({ sprintID })
    if (created) history.push(`/${businessID}/sprint-packets/${created.id}/edit`)
  }

  const renderRow = (packet: SprintPacket) => {
    const sprint = sprintsById.get(packet.sprintID)
    // The list's Present link bypasses the editor entirely, so the unreviewed
    // marker has to live here too — `customSections` rides the packet wire, so
    // it costs no extra query.
    const unreviewedCount = (packet.customSections ?? []).filter(s => s.needsReview).length
    const isReadOnly = packet.status === 'presented' || packet.status === 'archived'
    const detailRoute = `/${businessID}/sprint-packets/${packet.id}/${
      isReadOnly ? 'present' : 'edit'
    }`

    return (
      <tr key={packet.id} className="border-b last:border-b-0 hover:bg-gray-50 transition-colors">
        <td className="px-4 py-3 text-sm">
          <Link to={detailRoute} className="font-semibold text-gray-900 hover:text-purple-700">
            {sprint?.name ?? `Sprint #${packet.sprintID}`}
          </Link>
          <div className="text-xs text-gray-500 mt-0.5">{formatSprintWindow(sprint)}</div>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700">
          {statusLabel[packet.status]}
          {unreviewedCount > 0 && (
            <div
              className="text-xs font-semibold text-amber-700 mt-0.5"
              title="Slides carried forward from the previous packet that nobody has reviewed yet."
            >
              {unreviewedCount} slide{unreviewedCount === 1 ? '' : 's'} not reviewed
            </div>
          )}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700">{formatIsoDate(packet.createdAt)}</td>
        <td className="px-4 py-3 text-sm text-right">
          <div className="inline-flex items-center gap-2">
            <Link
              to={`/${businessID}/sprint-packets/${packet.id}/review`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Review
            </Link>
            <Link
              to={`/${businessID}/sprint-packets/${packet.id}/present`}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              Present
            </Link>
            <Link
              to={detailRoute}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-300 text-xs font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
            >
              {isReadOnly ? 'View' : 'Edit'}
            </Link>
          </div>
        </td>
      </tr>
    )
  }

  const newPacketButton = (
    <button
      type="button"
      onClick={() => setModalOpen(true)}
      className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold transition-colors"
    >
      <i className="fa fa-plus" />
      New packet
    </button>
  )

  return (
    <UserLayout>
      <FullPageHeader breadcrumbs={[{ title: 'Sprint Packets' }]}>
        <div className="ml-auto pr-4">{newPacketButton}</div>
      </FullPageHeader>
      <FullPageContent>
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <CircularProgress size={28} />
            </div>
          ) : packetsError ? (
            <div className="bg-white rounded-xl shadow-md p-10 flex flex-col items-center gap-3 text-center">
              <h2 className="text-lg font-bold text-gray-900">Couldn't load sprint packets</h2>
              <p className="text-sm text-gray-500 max-w-sm">
                Something went wrong fetching this business's packets. Try again in a moment.
              </p>
              <button
                type="button"
                onClick={() => refetchPackets()}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Retry
              </button>
            </div>
          ) : !packets || packets.length === 0 ? (
            <div className="bg-white rounded-xl shadow-md p-10 flex flex-col items-center gap-4 text-center">
              <h2 className="text-lg font-bold text-gray-900">No sprint packets yet</h2>
              <p className="text-sm text-gray-500 max-w-sm">
                Build a packet around an existing sprint to walk a client through performance,
                audiences, creative, and next steps.
              </p>
              {newPacketButton}
            </div>
          ) : (
            <div className="bg-white rounded-xl shadow-md overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Sprint
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody>{packets.map(renderRow)}</tbody>
              </table>
            </div>
          )}
        </div>
      </FullPageContent>
      {modalOpen && (
        <NewSprintPacketModal
          sprints={sprints ?? []}
          disabledSprintIDs={disabledSprintIDs}
          onCreate={handleCreate}
          onClose={() => setModalOpen(false)}
        />
      )}
    </UserLayout>
  )
}
